import { Button } from '@/components/ui/button'
import { Facts } from '@/components/ui/facts'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { useGhlBackfillRun, useGhlBackfillRuns, useStartGhlBackfill } from '@/hooks/useGoHighLevel'

const STATUS_LABEL = { queued: 'Waiting to start', running: 'Running', succeeded: 'Finished', failed: 'Stopped' } as const

/**
 * Start a venue's backfill and follow it (docs/gohighlevel.spec.md). Shown to CSE
 * super admins helping the company: it reads every lead the venue's
 * sub-accounts hold and sends events' and leads' details to Clef.
 */
export function GhlBackfillPanel({ venueId }: { venueId: string }) {
  const runs = useGhlBackfillRuns(venueId, true)
  const start = useStartGhlBackfill(venueId)
  const latestId = start.data?.id ?? runs.data?.runs[0]?.id ?? null
  const latest = useGhlBackfillRun(venueId, latestId).data
  const active = latest?.status === 'queued' || latest?.status === 'running'

  return (
    <div className="space-y-4">
      <p className="m-0 text-sm text-ink-muted">
        Links this venue’s existing events to their GoHighLevel leads: first by email, then by asking Clef about the
        rest. Clef links on its own when it’s 95% sure or more; anything less waits for review below.
      </p>
      <div className="flex items-center gap-3">
        <Button pending={active} size="sm" disabled={start.isPending || runs.isPending} onClick={() => start.mutate()}>
          Run backfill
        </Button>
        {latest ? (
          <span className="text-sm text-ink-muted">
            {STATUS_LABEL[latest.status]}
            {latest.startedByName !== null ? ` · started by ${latest.startedByName}` : ''} · {instantLabel(latest.createdAt)}
          </span>
        ) : null}
      </div>
      {start.error ? <FormError>{requestErrorMessage(start.error)}</FormError> : null}

      {latest ? (
        <>
          <Facts
            facts={[
              { label: 'Considered', value: String(latest.counts.considered), empty: '0' },
              { label: 'Linked by email', value: String(latest.counts.linkedByEmail), empty: '0' },
              { label: 'Linked by Clef', value: String(latest.counts.linkedByClef), empty: '0' },
              { label: 'Sent for review', value: String(latest.counts.sentToReview), empty: '0' },
              { label: 'Unmatched', value: String(latest.counts.unmatched), empty: '0' },
              { label: 'Not yet matched', value: String(latest.counts.notYetMatched), empty: '0' },
            ]}
          />
          {latest.error !== null ? <FormError>{latest.error}</FormError> : null}
          {latest.log !== '' ? (
            <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-surface p-3 text-xs">{latest.log}</pre>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
